import COLORS, { COLOR_VALUES } from "shared/config/colors";
import React, { useContext, useEffect, useMemo, useState } from "react";

import HStack from "shared/components/layout/stack/HStack";
import Loading from "shared/components/feedback/loading/Loading";
import ProjectsDashboardLayoutContext from "features/projects/contexts/ProjectsDashboardLayoutContext";
import SecondaryButton from "shared/components/inputs/button/SecondaryButton";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import { format } from "date-fns";
import { getCellWidthInPercent } from "features/admin/clients/ClientsTable";
import { getDropdownOptions } from "features/admin/offices/OfficesForm";
import { useProjectsNoteTypes } from "features/projects/project-dashboard/single/useProjectsDashboardNotes";

const tableTotalWidth = 1088;

const columns = [
    {
        headCellProps: {
            text: "",
            width: getCellWidthInPercent(42, tableTotalWidth),
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            icon:
                cell.value && !cell.row.original.isNoteVisited
                    ? "indicator"
                    : null,
            iconColor: COLORS.Yellow,
        }),
        accessor: "showNotificationIcon",
    },
    {
        headCellProps: {
            text: "",
            width: getCellWidthInPercent(160, tableTotalWidth),
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            text: cell.value
                ? format(new Date(cell.value), "MMMM dd, yyyy")
                : "-",
            description: cell.row.original.Creator,
        }),
        accessor: "Date",
    },
    {
        headCellProps: {
            text: "",
            width: getCellWidthInPercent(160, tableTotalWidth),
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
        }),
        accessor: "TypeName",
    },
    {
        headCellProps: {
            text: "",
            width: getCellWidthInPercent(672, tableTotalWidth),
        },
        disableSortBy: true,
        getBodyCellProps: (cell) => ({
            text: cell.value ?? "-",
            ellipsisAtLine: 3,
            cellTextOnMouseEnter: () =>
                cell.row.toggleRowExpanded(!cell.row.isExpanded),
            cellTextOnTextMouseLeave: () =>
                cell.row.toggleRowExpanded(!cell.row.isExpanded),
        }),
        enableExpanding: true,
        accessor: "Text",
    },
    {
        headCellProps: {
            width: getCellWidthInPercent(32, tableTotalWidth),
        },
        accessor: "Active",
        disableSortBy: true,

        getBodyCellProps: (cell) => ({
            shownByRowHover: true,
            actionIconWidth: 20,
            actionIcon: cell.row.original.isCreatorOfNote
                ? "edit"
                : "cursor-default",
            actionProps: {
                to: cell.row.original.isCreatorOfNote
                    ? `?action=edit&id=${cell.row.original.Id}`
                    : `?action=view&id`,
                replace: true,
                "aria-label": cell.row.original.isCreatorOfNote
                    ? "Edit"
                    : "View",
            },
        }),
    },
];

const tableCTAProps = {
    leadingIcon: "expand",
    label: "Add a Note",
    to: "?action=add",
    replace: true,
};

const getRowProps = (row) => ({
    style: {
        background: row.values.Active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const sortBy = [
    {
        id: "Date",
        desc: true,
    },
];

const dropdownNames = {
    type: "noteType",
};

const FiltersConfig = [
    {
        name: dropdownNames.type,
        columnAccessor: "TypeId",
        unfilteredValue: "all",
        isDropdownControl: true,
        formInputWrapperClassName: "max-width-160",
        dropdownFilterLabel: "Note Type",
    },
];

const ProjectsDashboardNotesTable = () => {
    const { projectsNoteTypes } = useProjectsNoteTypes();
    const { updatedNotesData, notesList, setFilters } = useContext(
        ProjectsDashboardLayoutContext
    );
    const [filtersConfigVal, setFiltersConfigVal] = useState(FiltersConfig);

    // When tab changes or component loads set initial state
    useEffect(() => {
        setFilters([]);
    }, [setFilters]);

    useEffect(() => {
        if (!projectsNoteTypes.isLoading) {
            setFiltersConfigVal((prevFilters) => {

                let newObj = [...prevFilters];

                const typeIndex = newObj.findIndex((f) => f.name === dropdownNames.type);

                newObj[typeIndex].options = getDropdownOptions(
                    projectsNoteTypes?.data,
                    "Id",
                    "Name",
                    "all",
                    "All"
                );

                return newObj[typeIndex].options.length > 0 ? prevFilters : newObj;
            });
        }
    }, [projectsNoteTypes]);

    const memoizedTable = useMemo(
        () => (
            <>
                {updatedNotesData ? (
                    <Table
                        noContentInHeaderRow={true}
                        dataIsFetching={notesList?.isFetching}
                        showTableBorder={false}
                        columns={columns}
                        data={updatedNotesData}
                        emptyMessageTableTitle={"Notes"}
                        getRowProps={getRowProps}
                        expandedAllRows={false}
                        sortBy={sortBy}
                    />
                ) : (
                    <Loading />
                )}
            </>
        ),
        [updatedNotesData, notesList]
    );

    if (projectsNoteTypes.isLoading) {
        return <Loading />;
    }

    if (notesList?.isError || projectsNoteTypes.isError) {
        return <>Error</>;
    }

    return (
        <VStack>
            <HStack align="end" justify="space-between">
                <HStack align="flex-end">
                    <TableFilters
                        filtersConfig={filtersConfigVal}
                        onChange={setFilters}
                    />
                </HStack>
                <SecondaryButton {...tableCTAProps} />
            </HStack>

            {memoizedTable}
        </VStack>
    );
};

export default React.memo(ProjectsDashboardNotesTable);
